import { z } from 'zod'

import { ShareScopeSchema } from '~/@entities/Content'
import { CurriculumSchema, TrieKeySchema } from '~/@entities/Curriculum'
import { PreviousExclusionScopeSchema } from '~/@entities/MakeCustomWorkbook'
import { PaperBoxSchema } from '~/@entities/Paper'
import {
  PracticeTestOptionSchema,
  ProblemAutoScoredTypeSchema,
  ProblemLevelSchema,
  ProblemSourceTypeSchema,
  ProblemTypeSchema,
  SimilarLevelSchema,
} from '~/@entities/Problem'
import {
  AllGradeSubjectSchema,
  RevisionSchema,
  SchoolTypeSchema,
  SemesterSchema,
} from '~/@entities/SchoolSystem'
import { SchoolTestTypeSchema } from '~/@entities/SchoolTest'
import { StudentIdSchema } from '~/@entities/Student'
import { WorkbookIdSchema } from '~/@entities/Workbook'
import {
  OriginWorkbookSchema,
  WorksheetMergeProblemSortTypeSchema,
  WorksheetPrintOptionSchema,
  WorksheetSchema,
  WorksheetTagSchema,
  WorksheetTypeSchema,
} from '~/@entities/Worksheet'

/***************** 개념 설명 위치 *****************/
// 유형 설명을 단원 앞에 모을지(CHAPTER) 문제 앞마다 둘지(FRONT)
export const CONCEPT_SORT_TYPE = ['CHAPTER', 'FRONT'] as const

/***************** 문제 형식 조건 *****************/
export const PROBLEM_FILTER_TYPE = ['CHOICE', 'SHORT_ANSWER', 'ALL'] as const

/***************** 균등 배분 단위 *****************/
// 문제를 어느 교육과정 단위로 고르게 나눌지
export const EQUALITY_LEVEL = ['BIG', 'MIDDLE', 'LITTLE', 'CONCEPT'] as const

/***************** 연산 난이도 *****************/
export const ARITHMETIC_LEVEL = ['LOW', 'MID_LOW', 'ALL'] as const

/***************** 시중교재 문제 조건 *****************/
// 교재 문제와 쌍둥이·유사 문제 중 무엇을 담을지
export const PUBLIC_WORKBOOK_PROBLEM_OPTION = ['ALL', 'SIMILAR', 'TWIN'] as const

/***************** 오답·원클릭 학습지의 문제 고르는 방식 *****************/
export const PROBLEM_REQUEST_TYPE = [
  'SIMILAR',
  'SAME',
  'SAME_PLUS_SIMILAR',
  'WRONG',
  'RANDOM',
] as const

/***************** 학생별 오답학습지 종류 *****************/
export const WRONG_WORKSHEET_TYPE = ['WRONG_CONCEPT_WORKSHEET', 'WRONG_PROBLEM_WORKSHEET'] as const

/***************** 원클릭 학습지 난이도 *****************/
export const ONE_CLICK_LEVEL_TYPE = ['EASY', 'NORMAL', 'HARD'] as const

/***************** 원클릭 학습지 문제 출처 *****************/
export const ONE_CLICK_PROBLEM_SOURCE_TYPE = ['WORKSHEET', 'LITTLE_CHAPTER'] as const
export const ONE_CLICK_PROBLEM_SOURCE_FILTER_TYPE = ['RECENTLY_SCORED'] as const

/***************** 나의DB 문항 처리 상태 *****************/
export const MY_DB_PROBLEM_PROCESSING_STATUS = [
  'NOT_REQUIRED',
  'OCR_PROCESSING',
  'IMAGE_PROCESSING',
  'COMPLETED',
  'FAILED',
] as const
export const MY_DB_PROBLEM_OCR_STATUS = [
  'NOT_REQUIRED',
  'PROCESSING',
  'COMPLETED',
  'FAILED',
] as const
export const MY_DB_PROBLEM_IMAGE_STATUS = ['PROCESSING', 'COMPLETED', 'FAILED'] as const
// 서버 ContentStatus(게시 상태)
export const MY_DB_PROBLEM_STATUS = ['DRAFT', 'ACTIVE', 'HIDDEN', 'INACTIVE'] as const

export const ConceptSortTypeSchema = z.enum(CONCEPT_SORT_TYPE)
export type ConceptSortType = z.infer<typeof ConceptSortTypeSchema>

export const ProblemFilterTypeSchema = z.enum(PROBLEM_FILTER_TYPE)
export type ProblemFilterType = z.infer<typeof ProblemFilterTypeSchema>

export const EqualityLevelSchema = z.enum(EQUALITY_LEVEL)
export type EqualityLevel = z.infer<typeof EqualityLevelSchema>

export const ArithmeticLevelSchema = z.enum(ARITHMETIC_LEVEL)
export type ArithmeticLevel = z.infer<typeof ArithmeticLevelSchema>

export const PublicWorkbookProblemOptionSchema = z.enum(PUBLIC_WORKBOOK_PROBLEM_OPTION)
export type PublicWorkbookProblemOption = z.infer<typeof PublicWorkbookProblemOptionSchema>

export const ProblemRequestTypeSchema = z.enum(PROBLEM_REQUEST_TYPE)
export type ProblemRequestType = z.infer<typeof ProblemRequestTypeSchema>

export const WrongWorksheetTypeSchema = z.enum(WRONG_WORKSHEET_TYPE)
export type WrongWorksheetType = z.infer<typeof WrongWorksheetTypeSchema>

export const OneClickLevelTypeSchema = z.enum(ONE_CLICK_LEVEL_TYPE)
export type OneClickLevelType = z.infer<typeof OneClickLevelTypeSchema>

export const OneClickProblemSourceTypeSchema = z.enum(ONE_CLICK_PROBLEM_SOURCE_TYPE)
export type OneClickProblemSourceType = z.infer<typeof OneClickProblemSourceTypeSchema>

export const OneClickProblemSourceFilterTypeSchema = z.enum(ONE_CLICK_PROBLEM_SOURCE_FILTER_TYPE)
export type OneClickProblemSourceFilterType = z.infer<typeof OneClickProblemSourceFilterTypeSchema>

export const MyDbProblemProcessingStatusSchema = z.enum(MY_DB_PROBLEM_PROCESSING_STATUS)
export type MyDbProblemProcessingStatus = z.infer<typeof MyDbProblemProcessingStatusSchema>

export const MyDbProblemOcrStatusSchema = z.enum(MY_DB_PROBLEM_OCR_STATUS)
export type MyDbProblemOcrStatus = z.infer<typeof MyDbProblemOcrStatusSchema>

export const MyDbProblemImageStatusSchema = z.enum(MY_DB_PROBLEM_IMAGE_STATUS)
export type MyDbProblemImageStatus = z.infer<typeof MyDbProblemImageStatusSchema>

export const MyDbProblemStatusSchema = z.enum(MY_DB_PROBLEM_STATUS)
export type MyDbProblemStatus = z.infer<typeof MyDbProblemStatusSchema>

/***************** 학습지에 담을 문제 *****************/

/**
 * 학습지 만들기가 추천하거나 학습지에 실려 있는 문제 한 칸. 문제은행의 Problem 과 달리 학습지 문맥(정답률·태그·
 * 출처·정렬 순서)이 붙는다. 서버가 함께 내는 groupCode·answer 는 앱이 읽지 않아 받지 않는다.
 */
export const MakeWorksheetProblemSchema = z.object({
  // 아직 문제로 복사되지 않은 나의DB 문항은 id 가 없다
  id: z.number().nullable(),
  type: ProblemTypeSchema,
  level: ProblemLevelSchema,
  rate: z.number(),
  problemImageUrl: z.string(),
  answerImageUrl: z.string(),
  solutionImageUrl: z.string(),
  autoScored: z.boolean(),
  autoScoredType: ProblemAutoScoredTypeSchema,
  trendy: z.boolean(),
  conceptId: z.number(),
  topicId: z.number().nullable(),
  subTopicId: z.number().nullable(),
  conceptName: z.string(),
  curriculumId: z.number(),
  // 서버 개념 캐시(CurriculumConceptCacheDto). 교육과정 노드에 개정이 더 붙는다. 그 밖의 캐시 키(topic·group 등)는
  // 앱이 읽지 않아 받지 않는다
  concept: CurriculumSchema.extend({ revision: RevisionSchema }),
  // 추천 문제에만 온다. 학습지에 실린 문제는 null 이다
  favorite: z.boolean().nullable(),
  tagTop: z.string().nullable(),
  sourceType: ProblemSourceTypeSchema,
  myDbProblemDetailId: z.number().nullable(),
  myDbPaperShareScope: ShareScopeSchema.nullable(),
  // 기출·기타 자료에서 온 문제의 박스. id 가 박스 번호다
  problemSource: z.object({ id: z.number(), problemImageUrl: z.string().nullable() }).nullable(),
  baseWorkbookProblemId: z.number().nullable(),
  defaultOrder: z.number().nullable(),
})
export type MakeWorksheetProblem = z.infer<typeof MakeWorksheetProblemSchema>

/***************** 나의DB 문항 *****************/

export const MyDbProblemLatestVersionSchema = z.object({
  versionId: z.number(),
  version: z.number(),
  imageStatus: MyDbProblemImageStatusSchema,
  contentDataUrl: z.string(),
  problemImageUrl: z.string().nullable(),
  answerImageUrl: z.string().nullable(),
  solutionImageUrl: z.string().nullable(),
  answer: z.string().nullable(),
  type: ProblemTypeSchema.nullable(),
  optionCount: z.number().nullable(),
  autoScoredType: ProblemAutoScoredTypeSchema,
})
export type MyDbProblemLatestVersion = z.infer<typeof MyDbProblemLatestVersionSchema>

/** 기출 업로드로 만든 나의DB 문항 한 건과 그 최신 버전. 편집 화면이 다음 버전을 올릴 자리까지 받는다 */
export const MyDbProblemDetailSchema = z.object({
  detailId: z.number(),
  conceptId: z.number(),
  conceptName: z.string().nullable(),
  topicId: z.number().nullable(),
  subTopicId: z.number().nullable(),
  level: ProblemLevelSchema,
  ocrStatus: MyDbProblemOcrStatusSchema,
  status: MyDbProblemStatusSchema,
  processingStatus: MyDbProblemProcessingStatusSchema,
  ocrRawDataUrl: z.string().nullable(),
  box: PaperBoxSchema.nullable(),
  originalImageUrls: z.array(z.string()).nullable(),
  latestVersion: MyDbProblemLatestVersionSchema.nullable(),
  problemType: ProblemTypeSchema.nullable(),
  tagTop: z.string().nullable(),
  nextVersionContentDataKey: z.string(),
  nextVersionContentDataUploadUrl: z.string(),
  paperTrieKey: TrieKeySchema.nullable().default(null),
  mixedContent: z.boolean().default(false),
})
export type MyDbProblemDetail = z.infer<typeof MyDbProblemDetailSchema>

/***************** 학습지 만들기 필터 *****************/
// 1단계에서 고른 조건. 서버가 필터마다 하위 클래스를 따로 두고 type 필드로만 가른다. 같은 조건이 두 곳에서 다른
// 모양으로 온다 — 만드는 중인 필터(GET /worksheet/filter, 캐시)는 학습지 메타와 담아 둔 문제까지 싣고, 저장된
// 학습지의 필터(학습지 상세)는 조건만 싣는다. 변형마다 그 필드만 두어 없는 키를 만들지 않는다 — 앱이 이 값을
// 필터 옵션 모델에 그대로 덮어써서, 없는 키를 null 로 채우면 모델의 기본값을 지운다.

const _localDate = z.iso.date()

/** 1단계 조건을 서버에 맡겨 두고 받는 키. 2·3단계와 수정·복제가 이것으로 조건을 다시 찾는다 */
export const WorksheetFilterIdSchema = z.string()
export type WorksheetFilterId = z.infer<typeof WorksheetFilterIdSchema>

const _OneClickProblemSourceSchema = z.object({
  type: OneClickProblemSourceTypeSchema,
  filterType: OneClickProblemSourceFilterTypeSchema,
  filterTypeOption: z.object({ filterCount: z.number() }),
})

const _selectedChapters = {
  // 1단계에서 고른 유형·소단원. 3단계 개념 설명에 쓴다
  selectedConceptIdList: z.array(z.number()),
  selectedLittleChapterIdList: z.array(z.number()),
}

const _excludePrevious = {
  excludePrevious: z.boolean(),
  previousExclusionScope: PreviousExclusionScopeSchema.nullable(),
  // 제외 단위가 STUDENT_ASSIGNED 일 때의 대상 학생
  studentIds: z.array(StudentIdSchema).nullable(),
}

const _conceptCondition = {
  conceptIdList: z.array(z.number()),
  problemCount: z.number(),
  problemFilterType: ProblemFilterTypeSchema,
  practiceTest: PracticeTestOptionSchema,
  onlyAutoScorable: z.boolean(),
  excludePrevious: z.boolean(),
  excludeOOC: z.boolean(),
  equalityLevel: EqualityLevelSchema.nullable(),
  minRate: z.number(),
  maxRate: z.number(),
  ..._selectedChapters,
}

// 연산이면 난이도 대신 연산 난이도를 쓰고 level·levelWeight 가 null 이다
const _arithmeticCondition = {
  level: ProblemLevelSchema.nullable(),
  levelWeight: z.array(z.number()).nullable(),
  arithmeticOnly: z.boolean(),
  arithmeticLevel: ArithmeticLevelSchema.nullable(),
}

const _leveledCondition = {
  level: ProblemLevelSchema,
  levelWeight: z.array(z.number()),
}

const _similarCondition = {
  pairX: z.number().nullable(),
  similarX: z.number().nullable(),
  similarLevel: SimilarLevelSchema,
}

const _schoolTestCondition = {
  conceptIdList: z.array(z.number()),
  schoolCodes: z.array(z.string()),
  schoolTestYears: z.array(z.number()).nullable(),
  schoolTestType: SchoolTestTypeSchema.nullable(),
  schoolTestRevision: RevisionSchema.nullable(),
  schoolTestSchoolType: SchoolTypeSchema.nullable(),
  schoolTestGrade: AllGradeSubjectSchema.nullable(),
  schoolTestSemester: SemesterSchema,
  problemFilterType: ProblemFilterTypeSchema,
  practiceTest: PracticeTestOptionSchema,
  onlyAutoScorable: z.boolean(),
  ..._excludePrevious,
  ..._similarCondition,
  ..._selectedChapters,
}

const _publicWorkbookCondition = {
  workbookId: WorkbookIdSchema,
  workbookProblemIdList: z.array(z.number()),
  problemFilterType: ProblemFilterTypeSchema,
  practiceTest: PracticeTestOptionSchema,
  onlyAutoScorable: z.boolean(),
  problemOption: PublicWorkbookProblemOptionSchema,
  ..._excludePrevious,
  ..._similarCondition,
  ..._selectedChapters,
}

const _practiceTestCondition = {
  practiceProblemIdList: z.array(z.number()),
  curriculumIdList: z.array(z.number()).nullable(),
  scoreList: z.array(z.number()),
  problemFilterType: ProblemFilterTypeSchema,
  onlyAutoScorable: z.boolean(),
  excludeOOC: z.boolean(),
  ..._selectedChapters,
}

const _paperCondition = {
  paperId: z.number(),
  selectedPageIndexes: z.array(z.number()),
  boxes: z.array(PaperBoxSchema),
  onlyAutoScorable: z.boolean(),
  ..._selectedChapters,
}

const _oneClickCondition = {
  conceptIdList: z.array(z.number()),
  problemCount: z.number(),
  studentId: StudentIdSchema,
  problemSource: _OneClickProblemSourceSchema,
  problemRequestType: ProblemRequestTypeSchema,
  problemFilterType: ProblemFilterTypeSchema,
  levelType: OneClickLevelTypeSchema,
  practiceTest: PracticeTestOptionSchema,
  onlyAutoScorable: z.boolean(),
  excludePrevious: z.boolean(),
  excludeOOC: z.boolean(),
  ..._selectedChapters,
}

const _mergedCondition = {
  onlyAutoScorable: z.boolean(),
  mergedWorksheetIds: z.array(WorksheetSchema.shape.id).nullable(),
  problemSortType: WorksheetMergeProblemSortTypeSchema,
}

/** 저장된 학습지의 필터(학습지 상세). 조건만 싣는다 */
export const SavedWorksheetFilterSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('CONCEPT'),
    ..._conceptCondition,
    ..._arithmeticCondition,
    ..._excludePrevious,
    source: z.string().nullable(),
  }),
  z.object({ type: z.literal('WEAK_CONCEPT'), ..._conceptCondition, ..._leveledCondition }),
  z.object({
    type: z.literal('WEAK_PERIOD'),
    ..._conceptCondition,
    ..._leveledCondition,
    startDate: _localDate,
    endDate: _localDate,
  }),
  z.object({ type: z.literal('WEAK_WORKSHEET'), ..._conceptCondition, ..._arithmeticCondition }),
  z.object({ type: z.literal('PRACTICE_TEST'), ..._practiceTestCondition }),
  z.object({ type: z.literal('PUBLIC_WORKBOOK'), ..._publicWorkbookCondition }),
  z.object({ type: z.literal('SCHOOL_TEST'), ..._schoolTestCondition }),
  z.object({ type: z.literal('CUSTOM_PAPER'), ..._paperCondition }),
  z.object({ type: z.literal('SCHOOL_TEST_PAPER'), ..._paperCondition }),
  z.object({ type: z.literal('ONE_CLICK'), ..._oneClickCondition }),
  z.object({ type: z.literal('MERGED_WORKSHEET'), ..._mergedCondition }),
  z.object({ type: z.literal('MY_DB_ORIGINAL'), myDbProblemDetailIds: z.array(z.number()) }),
  z.object({
    type: z.literal('MY_DB_SIMILAR'),
    myDbProblemDetailIds: z.array(z.number()),
    similarX: z.number(),
    similarLevel: SimilarLevelSchema,
    onlyAutoScorable: z.boolean(),
  }),
])
export type SavedWorksheetFilter = z.infer<typeof SavedWorksheetFilterSchema>

// 만드는 중인 필터가 조건 위에 더 싣는 것. 기출 업로드 두 종류만 학습지 메타가 반드시 있다
const _draftMeta = {
  title: z.string().nullable(),
  revision: RevisionSchema.nullable(),
  schoolType: SchoolTypeSchema.nullable(),
  grade: AllGradeSubjectSchema.nullable(),
  worksheetRange: z.string().nullable(),
  problemList: z.array(MakeWorksheetProblemSchema).nullable(),
}

const _paperDraftMeta = {
  title: z.string(),
  revision: RevisionSchema,
  schoolType: SchoolTypeSchema,
  grade: AllGradeSubjectSchema,
  worksheetRange: z.string(),
  trieKey: TrieKeySchema,
  problemList: z.array(MakeWorksheetProblemSchema).nullable(),
  problemFilterType: ProblemFilterTypeSchema,
  excludePrevious: z.boolean(),
}

const _myDbDraft = {
  title: z.string().nullable(),
  revision: RevisionSchema.nullable(),
  schoolType: SchoolTypeSchema.nullable(),
  grade: AllGradeSubjectSchema.nullable(),
  semester: SemesterSchema,
  myDbProblemDetailIds: z.array(z.number()).nullable(),
  myDbProblemDetails: z.array(MyDbProblemDetailSchema).nullable(),
  problemList: z.array(MakeWorksheetProblemSchema).nullable(),
}

/** 만드는 중인 학습지의 필터(filterId 로 찾는 것). 조건에 학습지 메타와 담아 둔 문제가 더 붙는다 */
export const WorksheetFilterDraftSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('CONCEPT'),
    ..._draftMeta,
    ..._conceptCondition,
    ..._arithmeticCondition,
    ..._excludePrevious,
    includeMyDbProblem: z.boolean(),
    source: z.string().nullable(),
    excludedTopicIds: z.array(z.number()).nullable(),
    excludedSubTopicIds: z.array(z.number()).nullable(),
  }),
  z.object({
    type: z.literal('WEAK_CONCEPT'),
    ..._draftMeta,
    ..._conceptCondition,
    ..._leveledCondition,
  }),
  z.object({
    type: z.literal('WEAK_PERIOD'),
    ..._draftMeta,
    ..._conceptCondition,
    ..._leveledCondition,
    startDate: _localDate,
    endDate: _localDate,
  }),
  z.object({
    type: z.literal('WEAK_WORKSHEET'),
    ..._draftMeta,
    ..._conceptCondition,
    ..._arithmeticCondition,
  }),
  z.object({ type: z.literal('PRACTICE_TEST'), ..._draftMeta, ..._practiceTestCondition }),
  z.object({
    type: z.literal('PUBLIC_WORKBOOK'),
    ..._draftMeta,
    ..._publicWorkbookCondition,
    semester: SemesterSchema,
    exampleProblemIds: z.array(z.number()),
    includeSameProblem: z.boolean(),
    originWorkbook: OriginWorkbookSchema.nullable(),
  }),
  z.object({ type: z.literal('SCHOOL_TEST'), ..._draftMeta, ..._schoolTestCondition }),
  z.object({
    type: z.literal('CUSTOM_PAPER'),
    ..._paperCondition,
    ..._paperDraftMeta,
    paperTitle: z.string(),
  }),
  z.object({
    type: z.literal('SCHOOL_TEST_PAPER'),
    ..._paperCondition,
    ..._paperDraftMeta,
    schoolName: z.string(),
    schoolTestYear: z.number(),
    schoolTestType: SchoolTestTypeSchema,
  }),
  z.object({ type: z.literal('ONE_CLICK'), ..._draftMeta, ..._oneClickCondition }),
  z.object({ type: z.literal('MERGED_WORKSHEET'), ..._draftMeta, ..._mergedCondition }),
  z.object({
    type: z.literal('MY_DB_ORIGINAL'),
    ..._myDbDraft,
    // 학습지에 실린 문제 id → 나의DB 문항 id. 키는 문자열이 된 문제 id 다
    problemIdToDetailIdMap: z.record(z.string(), z.number()).nullable(),
  }),
  z.object({
    type: z.literal('MY_DB_SIMILAR'),
    ..._myDbDraft,
    similarX: z.number(),
    similarLevel: SimilarLevelSchema,
  }),
])
export type WorksheetFilterDraft = z.infer<typeof WorksheetFilterDraftSchema>

/***************** 학습지 상세 *****************/

/** 학습지 PDF 의 디자인 옵션과 쪽수 */
export const WorksheetPdfSchema = WorksheetPrintOptionSchema.extend({
  worksheetId: WorksheetSchema.shape.id,
  designTemplateId: z.number().nullable(),
  problemPadding: z.number(),
  pdfDate: _localDate.nullable(),
  conceptSortType: ConceptSortTypeSchema.nullable(),
  elementPage: z.number(),
  answerSolutionPage: z.number(),
  omrPage: z.number(),
  quickAnswerPage: z.number(),
  // 아직 만들어지지 않았으면 빈 문자열이다
  pdfUrl: z.string(),
  version: z.number(),
})
export type WorksheetPdf = z.infer<typeof WorksheetPdfSchema>

/**
 * 학습지 하나의 메타·PDF·실린 문제·만들 때 쓴 필터. 수정·복제가 이것으로 학습지 만들기를 다시 연다.
 * 학교급은 school 로 온다.
 */
export const WorksheetDetailSchema = z.object({
  id: WorksheetSchema.shape.id,
  type: WorksheetTypeSchema,
  tag: WorksheetTagSchema,
  level: ProblemLevelSchema,
  revision: RevisionSchema,
  school: SchoolTypeSchema,
  grade: AllGradeSubjectSchema,
  title: z.string(),
  titlePrefix: z.string(),
  autoScorableFlag: z.boolean(),
  chapter: z.string(),
  writer: z.string(),
  createDatetime: z.iso.datetime({ local: true }),
  problems: z.array(MakeWorksheetProblemSchema),
  pdf: WorksheetPdfSchema.nullable(),
  // 학습지 만들기로 만든 학습지(CUSTOM·CREVERSE_ENTRANCE)는 저장된 필터를, 그 밖의 학습지는 서버가 학습지에서 되짚어
  // 만든 단원·유형 필터를 낸다. 만들기로 만들었어도 필터 행이 없으면 null 이다
  filter: SavedWorksheetFilterSchema.nullable(),
})
export type WorksheetDetail = z.infer<typeof WorksheetDetailSchema>
